import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { loadPaymentWidget } from '@tosspayments/payment-widget-sdk';
import { Payments as PaymentsIcon, CheckCircleOutline, EventAvailable } from '@mui/icons-material';
import { useAuth } from '../../../hooks/auth/useAuth';
import {
  useTossMonthlyPayment,
  isSubscriptionPeriodActive,
} from '../../../hooks/payment/useTossMonthlyPayment';
import { usePaymentSettings } from '../../../hooks/payment/usePaymentSettings';
import { isTossWidgetClientKey } from '../../../utils/tossKeys';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';

const functions = getFunctions(getApp(), 'asia-northeast3');
const getOrCreateTossCustomerKeyFn = httpsCallable(functions, 'getOrCreateTossCustomerKey');

const PageShell = styled.div`
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  padding: 8px 0 40px;
`;

const PageTitle = styled.div`
  margin-bottom: 22px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  .MuiSvgIcon-root {
    font-size: 28px;
    color: #1d2340;
  }
`;

const H1 = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
`;

const Sub = styled.p`
  margin: 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
`;

const IntroBanner = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  margin-bottom: 20px;
  border-radius: 14px;
  background: #fffbea;
  border: 1px solid rgba(231, 225, 211, 1);
  color: #1d2340;
  font-size: 13px;
  line-height: 1.55;
  font-weight: 600;
`;

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(255, 229, 138, 0.55);
  color: #1d2340;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const BannerClose = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;
  opacity: 0.7;
  display: flex;
  &:hover {
    opacity: 1;
  }
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  margin-bottom: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 260px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #1d2340;
  border-radius: 50%;
  animation: paySpin 0.8s linear infinite;
  @keyframes paySpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 15px;
  color: #64748b;
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  gap: 20px;
  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
    align-items: start;
  }
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
`;

const CardTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  span {
    font-size: 1rem;
    font-weight: 600;
    color: #64748b;
    margin-left: 4px;
  }
`;

const PlanList = styled.ul`
  margin: 16px 0 0;
  padding-left: 20px;
  color: #475569;
  font-size: 14px;
  line-height: 1.7;
`;

const PrimaryButton = styled.button`
  width: 100%;
  margin-top: 20px;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  background: #1d2340;
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(29, 35, 64, 0.18);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  ${(p) =>
    p.$active
      ? `background: #ecfdf5; color: #047857;`
      : `background: #fef3c7; color: #b45309;`}
`;

const Muted = styled.p`
  margin: 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
`;

const WidgetBox = styled.div`
  margin-top: 20px;
  padding: 16px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const WidgetHint = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: #64748b;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th,
  td {
    padding: 12px 10px;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }
  th {
    color: #64748b;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const EmptyHist = styled.p`
  margin: 0;
  padding: 20px 0;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const HistoryCards = styled.div`
  display: none;
  flex-direction: column;
  gap: 10px;
  @media (max-width: 640px) {
    display: flex;
  }
`;

const HistCard = styled.div`
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const HistCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #475569;
  margin-bottom: 6px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const HistCardLabel = styled.span`
  font-weight: 700;
  color: #64748b;
`;

const HistCardValue = styled.span`
  font-weight: 600;
  color: #0f172a;
  text-align: right;
`;

const DesktopTable = styled.div`
  @media (max-width: 640px) {
    display: none;
  }
`;

const formatMoney = (n) => (typeof n === 'number' ? `${n.toLocaleString('ko-KR')}원` : '-');

const daysUntil = (raw) => {
  if (!raw) return null;
  const date = raw.seconds ? new Date(raw.seconds * 1000) : raw._seconds ? new Date(raw._seconds * 1000) : new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

const formatDate = (raw) => {
  if (!raw) return '-';
  try {
    if (raw.seconds) return new Date(raw.seconds * 1000).toLocaleString('ko-KR');
    if (raw._seconds) return new Date(raw._seconds * 1000).toLocaleString('ko-KR');
    return new Date(raw).toLocaleString('ko-KR');
  } catch {
    return '-';
  }
};

const PaymentManagement = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { amount: planAmount, orderName: planOrderName, loading: settingsLoading, error: settingsError } =
    usePaymentSettings();
  const { subscription, history, loading, startMonthlyPayment, refresh } = useTossMonthlyPayment(user, {
    amount: planAmount,
    orderName: planOrderName,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [banner, setBanner] = useState(null);
  const [inlineError, setInlineError] = useState(null);
  const [widgetError, setWidgetError] = useState(null);
  const [widgetReady, setWidgetReady] = useState(false);
  const widgetInstanceRef = useRef(null);
  const paymentMethodWidgetRef = useRef(null);

  const rawClientKey = process.env.REACT_APP_TOSS_CLIENT_KEY?.trim();
  const useWidget = Boolean(rawClientKey && isTossWidgetClientKey(rawClientKey));
  const clientKeyMissing = !rawClientKey;

  const initWidget = useCallback(async () => {
    if (!useWidget || !user?.uid || !rawClientKey) return;
    setWidgetError(null);
    setWidgetReady(false);
    try {
      const { data } = await getOrCreateTossCustomerKeyFn();
      const customerKey = data.customerKey;
      const widgets = await loadPaymentWidget(rawClientKey, customerKey);
      widgetInstanceRef.current = widgets;

      const variantKey = process.env.REACT_APP_TOSS_WIDGET_VARIANT_KEY || 'DEFAULT';
      const pm = await widgets.renderPaymentMethods(
        '#toss-widget-payment-methods',
        { currency: 'KRW', value: Number(planAmount) },
        { variantKey }
      );
      paymentMethodWidgetRef.current = pm;

      await widgets.renderAgreement('#toss-widget-agreement');
      setWidgetReady(true);
    } catch (e) {
      console.error(e);
      setWidgetError(e?.message || '결제위젯을 불러오지 못했습니다.');
    }
  }, [useWidget, user?.uid, rawClientKey, planAmount]);

  useEffect(() => {
    if (!useWidget || !user?.uid) return undefined;
    (async () => {
      await initWidget();
    })();
    return () => {
      const destroy = async () => {
        try {
          await paymentMethodWidgetRef.current?.destroy?.();
        } catch (_) {
          /* noop */
        }
        paymentMethodWidgetRef.current = null;
        widgetInstanceRef.current = null;
      };
      destroy();
    };
  }, [useWidget, user?.uid, initWidget]);

  useEffect(() => {
    if (location.state?.paid) {
      setBanner('결제가 완료되었습니다. 이용 기간이 갱신되었습니다.');
      setInlineError(null);
      refresh();
      window.history.replaceState({}, document.title);
    }
  }, [location.state, refresh]);

  const periodActive = isSubscriptionPeriodActive(subscription);

  const handlePayWindow = async () => {
    if (clientKeyMissing || useWidget) return;
    setInlineError(null);
    setActionLoading(true);
    try {
      await startMonthlyPayment();
    } catch (e) {
      setInlineError(e?.message || '결제창을 열 수 없습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayWidget = async () => {
    const widgets = widgetInstanceRef.current;
    if (!widgets || !user?.uid) return;
    setInlineError(null);
    setActionLoading(true);
    try {
      const orderId = `pay_${user.uid}_${Date.now()}`;
      const origin = window.location.origin;
      const customerName =
        user?.displayName || (user?.email ? user.email.split('@')[0] : '') || '회원';
      await widgets.requestPayment({
        orderId,
        orderName: planOrderName,
        successUrl: `${origin}/payment/success`,
        failUrl: `${origin}/payment/fail`,
        customerEmail: user?.email || undefined,
        customerName,
      });
    } catch (e) {
      setInlineError(e?.message || '결제 요청에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || settingsLoading) {
    return (
      <PageShell>
        <LoadingCard>
          <LoadingSpinner />
          <LoadingText>결제 정보를 불러오는 중...</LoadingText>
        </LoadingCard>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageTitle>
        <TitleRow>
          <PaymentsIcon aria-hidden />
          <H1>수강권</H1>
        </TitleRow>
        <Sub>
          토스페이먼츠로 <strong>월 이용권</strong>을 결제합니다.{' '}
          {useWidget ? (
            <>아래에서 결제 수단과 약관에 동의한 뒤 <strong>월 이용권 결제하기</strong>를 누르세요.</>
          ) : (
            <>매월 이 페이지에서 직접 결제합니다. 자동결제(빌링)는 사용하지 않습니다.</>
          )}
        </Sub>
      </PageTitle>

      <IntroBanner>
        <ReceiptLongOutlined style={{ fontSize: 22, flexShrink: 0, color: '#2c3561' }} aria-hidden />
        <span>
          결제 내역은 아래 목록에서 확인할 수 있습니다. 모바일에서는 카드 형태로 보입니다.
        </span>
      </IntroBanner>

      {settingsError && (
        <IntroBanner style={{ borderColor: '#fcd34d', background: '#fffbeb' }}>
          <span style={{ color: '#92400e' }}>
            서버에서 결제 금액을 불러오지 못했습니다. 표시는 앱 기본값일 수 있습니다. ({settingsError})
          </span>
        </IntroBanner>
      )}

      {banner && (
        <Banner role="status">
          <span>{banner}</span>
          <BannerClose type="button" onClick={() => setBanner(null)} aria-label="배너 닫기">
            <CloseRounded />
          </BannerClose>
        </Banner>
      )}

      {inlineError && (
        <ErrorBanner role="alert">
          <span>{inlineError}</span>
          <BannerClose type="button" onClick={() => setInlineError(null)} aria-label="오류 메시지 닫기">
            <CloseRounded />
          </BannerClose>
        </ErrorBanner>
      )}

      <Grid>
        <Card>
          <CardTitle>월 이용권</CardTitle>
          <PlanPrice>
            {planAmount.toLocaleString('ko-KR')}
            <span>/1개월</span>
          </PlanPrice>
          <Muted style={{ marginTop: 10, fontSize: 13 }}>{planOrderName}</Muted>
          <PlanList>
            <li>결제 완료 시점부터 1개월간 이용 가능</li>
            <li>만료 후에도 이 페이지에서 다시 결제하여 연장</li>
            <li>강의, AI 첨삭, 리라이팅을 이용 기간 동안 사용</li>
          </PlanList>

          {periodActive ? (
            <Muted style={{ marginTop: 20 }}>
              <StatusBadge $active>
                <CheckCircleOutline style={{ fontSize: 18 }} />
                이용 기간 안
              </StatusBadge>
            </Muted>
          ) : (
            <Muted style={{ marginTop: 20 }}>
              <StatusBadge $active={false}>
                <EventAvailable style={{ fontSize: 18 }} />
                이용 기간 만료 또는 미결제
              </StatusBadge>
            </Muted>
          )}

          {subscription?.validUntil && (
            <Muted style={{ marginTop: 12 }}>
              <strong>이용 종료:</strong> {formatDate(subscription.validUntil)}
              {daysUntil(subscription.validUntil) !== null && (
                <> · {Math.max(daysUntil(subscription.validUntil), 0)}일 남음</>
              )}
            </Muted>
          )}

          {clientKeyMissing && (
            <Muted style={{ marginTop: 12, color: '#b45309' }}>
              <code>REACT_APP_TOSS_CLIENT_KEY</code>를 설정해 주세요. 위젯은 <code>gck</code>가 포함된
              키, 결제창만 쓸 때는 <code>ck</code> 키입니다.
            </Muted>
          )}

          {useWidget ? (
            <WidgetBox>
              <WidgetHint>
                결제수단과 약관에 동의한 뒤 아래 버튼으로 결제를 진행합니다.
              </WidgetHint>
              {widgetError && (
                <Muted style={{ color: '#b91c1c', marginBottom: 12 }}>{widgetError}</Muted>
              )}
              <div id="toss-widget-payment-methods" />
              <div id="toss-widget-agreement" style={{ marginTop: 16 }} />
              <PrimaryButton
                type="button"
                disabled={actionLoading || !widgetReady || !!widgetError}
                onClick={handlePayWidget}
              >
                {actionLoading ? '처리 중...' : widgetReady ? '월 이용권 결제하기' : '위젯 로딩 중...'}
              </PrimaryButton>
            </WidgetBox>
          ) : (
            <PrimaryButton
              type="button"
              disabled={actionLoading || clientKeyMissing}
              onClick={handlePayWindow}
            >
              {actionLoading ? '처리 중...' : '월 이용권 결제하기 (결제창)'}
            </PrimaryButton>
          )}
        </Card>

        <Card>
          <CardTitle>결제 내역</CardTitle>
          {history.length === 0 ? (
            <EmptyHist>아직 결제 내역이 없습니다.</EmptyHist>
          ) : (
            <>
              <HistoryCards>
                {history.map((row) => (
                  <HistCard key={row.id}>
                    <HistCardRow>
                      <HistCardLabel>일시</HistCardLabel>
                      <HistCardValue>{formatDate(row.createdAt)}</HistCardValue>
                    </HistCardRow>
                    <HistCardRow>
                      <HistCardLabel>항목</HistCardLabel>
                      <HistCardValue>{row.orderName || '월 이용권'}</HistCardValue>
                    </HistCardRow>
                    <HistCardRow>
                      <HistCardLabel>금액</HistCardLabel>
                      <HistCardValue>{formatMoney(row.amount)}</HistCardValue>
                    </HistCardRow>
                  </HistCard>
                ))}
              </HistoryCards>
              <DesktopTable>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>일시</th>
                        <th>항목</th>
                        <th>금액</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDate(row.createdAt)}</td>
                          <td>{row.orderName || '월 이용권'}</td>
                          <td>{formatMoney(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </DesktopTable>
            </>
          )}
        </Card>
      </Grid>
    </PageShell>
  );
};

export default PaymentManagement;
